/* File: frontend/src/components/reputation/ContentStrategyPanel.js */
/* Component for Content Strategy Panel */
/* Version: v1.4 (Refactor: Removed Master Prompt UI - Managed via Brain Center) */
/* Date: 2025-12-21 */
/* Description: 
   - Панель управления стратегией контента (Content Factory).
   - [Refactor]: Removed "Master Prompt" editor. Prompt is now managed via Admin Brain Center.
*/

import React, { useState, useEffect } from 'react';
import { Brain, Calendar, Layout, Save, RefreshCcw, CheckCircle, Eye } from 'lucide-react';
import apiService from '../../services/apiService';

const styles = {
    panel: {
        backgroundColor: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        padding: '25px',
        marginBottom: '20px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
    },
    header: {
        display: 'flex', alignItems: 'center', gap: '10px',
        fontSize: '1.2rem', fontWeight: '700', color: '#1e293b', marginBottom: '20px',
        paddingBottom: '15px', borderBottom: '1px solid #f1f5f9'
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: '1fr', // Single column layout now
        gap: '30px'
    },
    label: {
        display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '8px'
    },
    controlGroup: { marginBottom: '25px' },
    sliderRow: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px'
    },
    badge: {
        backgroundColor: '#e0f2fe', color: '#0284c7',
        padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '700',
        border: '1px solid #bae6fd'
    },
    toggleRow: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '15px', backgroundColor: '#f0fdf4', borderRadius: '8px',
        border: '1px solid #bbf7d0', marginBottom: '15px'
    },
    // Dynamic styles for main toggle
    activeToggleRowOff: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '15px', backgroundColor: '#f8fafc', borderRadius: '8px',
        border: '1px solid #e2e8f0', marginBottom: '15px', transition: 'all 0.3s ease'
    },
    activeToggleRowOn: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '15px', backgroundColor: '#eff6ff', borderRadius: '8px',
        border: '1px solid #3b82f6', marginBottom: '15px', transition: 'all 0.3s ease',
        boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.1)'
    },
    saveBtn: {
        width: '100%', padding: '12px', backgroundColor: '#4f46e5', color: 'white',
        border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        fontSize: '0.9rem',
        transition: 'background 0.2s, transform 0.1s',
        boxShadow: '0 4px 6px -1px rgba(79, 70, 229, 0.2)'
    }
};

const ContentStrategyPanel = ({ initialSettings, onSave, locationId = null }) => {
    // Безопасная инициализация настроек
    const [settings, setSettings] = useState({
        daily_limit: 3,
        posts_per_hour: 1,
        cornerstone_first: true,
        active: false,
        ...(initialSettings || {})
    });
    // State for Button Hover
    const [hoverSave, setHoverSave] = useState(false);

    // Загрузка задач
    const [tasks, setTasks] = useState([]);
    useEffect(() => {
        const fetchTasks = async () => {
            try {
                let endpoint = '/reputation/tasks';
                if (locationId) {
                    endpoint += `?location_id=${locationId}`;
                }

                const data = await apiService.makeRequest(endpoint);
                setTasks(data);
            } catch (e) { console.error("Failed to load tasks", e); }
        };
        fetchTasks();
        const interval = setInterval(fetchTasks, 10000);
        return () => clearInterval(interval);
    }, [locationId]);

    // Обработчик изменений полей
    const handleChange = (field, value) => {
        setSettings(prev => ({ ...prev, [field]: value }));
    };

    return (
        <div style={styles.panel}>
            {/* UNIFIED SCROLLBAR STYLE */}
            <style>{`
                .smart-feed-scroll::-webkit-scrollbar { width: 6px; }
                .smart-feed-scroll::-webkit-scrollbar-track { background: transparent; }
                .smart-feed-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
                .smart-feed-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
            `}</style>

            <div style={styles.header}>
                <Brain size={24} color="#4f46e5" />
                <span>Content Factory Strategy (AI Writer)</span>
            </div>

            {/* STRATEGIC TASK QUEUE */}
            <div style={{ marginBottom: '30px', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                <div style={{ padding: '12px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.9rem' }}>Strategic Rep-Fix Queue (Autonomous)</div>
                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{tasks.length} Active Tasks</div>
                </div>

                <div className="smart-feed-scroll" style={{ maxHeight: '450px', overflowY: 'auto', overflowX: 'hidden' }}>
                    {tasks.length === 0 ? (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>No active strategic tasks. Run an Audit to queue fixes.</div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                            <thead style={{ background: '#f1f5f9', color: '#475569', textAlign: 'left', position: 'sticky', top: 0, zIndex: 1 }}>
                                <tr>
                                    <th style={{ padding: '10px 20px', fontWeight: '600' }}>Status</th>
                                    <th style={{ padding: '10px 20px', fontWeight: '600' }}>Target Query</th>
                                    <th style={{ padding: '10px 20px', fontWeight: '600' }}>Strategy Recipe</th>
                                    <th style={{ padding: '10px 20px', fontWeight: '600' }}>Progress</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tasks.map(task => (
                                    <tr key={task.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '12px 20px' }}>
                                            {task.status === 'queued_pending' && <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#d97706', fontWeight: '600' }}><div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#f59e0b' }}></div> Queued</span>}
                                            {task.status === 'processing_ai' && <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#2563eb', fontWeight: '600' }}><RefreshCcw size={14} className="spin-animation" /> Generating...</span>}
                                            {task.status === 'published_monitoring' && <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#7c3aed', fontWeight: '600' }}><Eye size={14} /> Monitoring</span>}
                                            {task.status === 'resolved_top5' && <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#16a34a', fontWeight: '600' }}><CheckCircle size={14} /> Resolved</span>}
                                        </td>
                                        <td style={{ padding: '12px 20px', fontWeight: '500', color: '#334155' }}>{task.query}</td>
                                        <td style={{ padding: '12px 20px', color: '#64748b' }}>
                                            {task.recipe?.type || 'Cornerstone'} ({task.recipe?.word_count || '2k'} w)
                                        </td>
                                        <td style={{ padding: '12px 20px' }}>
                                            {task.status === 'published_monitoring' ? (
                                                <span style={{ background: '#f3e8ff', color: '#7c3aed', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold' }}>Rank #{task.current_rank || '?'}</span>
                                            ) : (
                                                <span style={{ color: '#cbd5e1' }}>-</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            <div style={styles.grid}>
                {/* RULES & SCHEDULE (Full Width Now) */}
                <div>
                    <label style={{ ...styles.label, borderBottom: '2px solid #e2e8f0', paddingBottom: '10px', marginBottom: '20px' }}>
                        ⚙️ Posting Schedule & Rules
                    </label>

                    {/* Daily Limit Slider */}
                    <div style={styles.controlGroup}>
                        <div style={styles.sliderRow}>
                            <span style={{ fontSize: '0.9rem', color: '#334155' }}>Daily Limit</span>
                            <span style={styles.badge}>{settings.daily_limit} posts/day</span>
                        </div>
                        <input
                            type="range" min="1" max="10" step="1"
                            value={settings.daily_limit}
                            onChange={(e) => handleChange('daily_limit', parseInt(e.target.value, 10))}
                            style={{ width: '100%', cursor: 'pointer' }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', color: '#94a3b8', marginTop: '2px' }}>
                            <span>1 post</span><span>5 posts</span><span>10 posts</span>
                        </div>
                    </div>

                    {/* AI POWER LEVEL */}
                    <div style={{ ...styles.controlGroup, background: settings.ai_power === 3 ? '#FEF2F2' : 'transparent', padding: settings.ai_power === 3 ? '10px' : '0', borderRadius: '8px', border: settings.ai_power === 3 ? '1px solid #FCA5A5' : 'none', transition: 'all 0.3s' }}>
                        <div style={styles.sliderRow}>
                            <span style={{ fontSize: '0.9rem', color: settings.ai_power === 3 ? '#B91C1C' : '#334155', fontWeight: settings.ai_power === 3 ? '700' : '400' }}>
                                {settings.ai_power === 3 ? '☢️ NUCLEAR POWER' : 'AI Power Level'}
                            </span>
                            <span style={{
                                ...styles.badge,
                                backgroundColor: settings.ai_power === 3 ? '#FECACA' : (settings.ai_power === 2 ? '#E0E7FF' : '#F1F5F9'),
                                color: settings.ai_power === 3 ? '#B91C1C' : (settings.ai_power === 2 ? '#4338CA' : '#64748b'),
                                border: 'none'
                            }}>
                                {(!settings.ai_power || settings.ai_power === 1) && 'Standard'}
                                {settings.ai_power === 2 && 'Deep Dive'}
                                {settings.ai_power === 3 && 'NUCLEAR GUARANTEE'}
                            </span>
                        </div>
                        <input
                            type="range" min="1" max="3" step="1"
                            value={settings.ai_power || 1}
                            onChange={(e) => handleChange('ai_power', parseInt(e.target.value, 10))}
                            style={{ width: '100%', cursor: 'pointer', accentColor: settings.ai_power === 3 ? '#EF4444' : '#4F46E5' }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', color: settings.ai_power === 3 ? '#EF4444' : '#94a3b8', marginTop: '2px', fontWeight: settings.ai_power === 3 ? '600' : '400' }}>
                            <span>Standard</span><span>Deep</span><span>NUCLEAR</span>
                        </div>
                        {settings.ai_power === 3 && (
                            <div style={{ fontSize: '0.7rem', color: '#B91C1C', marginTop: '6px', lineHeight: '1.2' }}>
                                Warning: Max token usage. Multi-step reasoning + self-critique enabled.
                            </div>
                        )}
                    </div>

                    {/* Frequency Slider */}
                    <div style={styles.controlGroup}>
                        <div style={styles.sliderRow}>
                            <span style={{ fontSize: '0.9rem', color: '#334155' }}>Speed (Pacing)</span>
                            <span style={styles.badge}>{settings.posts_per_hour} post/hour</span>
                        </div>
                        <input
                            type="range" min="1" max="5" step="1"
                            value={settings.posts_per_hour}
                            onChange={(e) => handleChange('posts_per_hour', parseInt(e.target.value, 10))}
                            style={{ width: '100%', cursor: 'pointer' }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', color: '#94a3b8', marginTop: '2px' }}>
                            <span>Slow (1)</span><span>Turbo (5)</span>
                        </div>
                    </div>

                    {/* Cornerstone Strategy Toggle */}
                    <div style={styles.toggleRow}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                            <Layout size={20} color="#16a34a" style={{ marginTop: '2px' }} />
                            <div>
                                <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#166534' }}>Cornerstone First</div>
                                <div style={{ fontSize: '0.75rem', color: '#15803d', lineHeight: '1.3', marginTop: '2px' }}>
                                    Prioritize creating long-form authority content before local news updates.
                                </div>
                            </div>
                        </div>
                        <input
                            type="checkbox"
                            checked={settings.cornerstone_first}
                            onChange={(e) => handleChange('cornerstone_first', e.target.checked)}
                            style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                        />
                    </div>

                    {/* Main Activation Toggle */}
                    <div style={settings.active ? styles.activeToggleRowOn : styles.activeToggleRowOff}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                            {settings.active ? (
                                <CheckCircle size={24} color="#3b82f6" style={{ marginTop: '2px' }} />
                            ) : (
                                <Calendar size={24} color="#94a3b8" style={{ marginTop: '2px' }} />
                            )}
                            <div>
                                <div style={{ fontSize: '0.9rem', fontWeight: '700', color: settings.active ? '#1e40af' : '#64748b' }}>
                                    {settings.active ? 'Auto-Poster ACTIVE' : 'Enable Auto-Poster'}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: settings.active ? '#3b82f6' : '#94a3b8', lineHeight: '1.3', marginTop: '2px' }}>
                                    {settings.active
                                        ? 'System is running background tasks.'
                                        : 'System is paused. No automatic posts.'}
                                </div>
                            </div>
                        </div>
                        <label className="switch">
                            <input
                                type="checkbox"
                                checked={settings.active}
                                onChange={(e) => handleChange('active', e.target.checked)}
                            />
                            <span className="slider"></span>
                        </label>
                    </div>

                    <button
                        style={{
                            ...styles.saveBtn,
                            backgroundColor: hoverSave ? '#8b5cf6' : '#a78bfa', // Violet 500 : Violet 400
                            transform: hoverSave ? 'scale(1.02)' : 'scale(1)'
                        }}
                        onMouseEnter={() => setHoverSave(true)}
                        onMouseLeave={() => setHoverSave(false)}
                        onClick={() => onSave(settings)}
                    >
                        <Save size={18} /> Save Strategy Configuration
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ContentStrategyPanel;